import defaultMoment from 'moment';
import { humanize } from '@utils/humanizeUtil';
import { findAxisIndex, getAxisLabelFormat, tooltipFormatter } from '../hooks/utils';
import { TIME_AXIS, VALUE_AXIS, METRIC_SERIES } from './constants';
import { transparentize } from 'polished';

/** Y-axis only: bps must use humanize(..., 'bps') plus kb/s · Mb/s labels (avoid default "B" billion prefix). */
function formatBpsYAxisLabel(value: number): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }
  const raw = humanize(value, 'bps');
  if (raw == null) {
    return '';
  }
  const s = String(raw).trim();
  if (/\sK$/.test(s)) {
    return s.replace(/\sK$/, ' kb/s');
  }
  if (/\sM$/.test(s)) {
    return s.replace(/\sM$/, ' Mb/s');
  }
  if (/\sG$/.test(s)) {
    return s.replace(/\sG$/, ' Gb/s');
  }
  if (/\sT$/.test(s)) {
    return s.replace(/\sT$/, ' Tb/s');
  }
  if (/\sP$/.test(s)) {
    return s.replace(/\sP$/, ' Pb/s');
  }
  if (/\sE$/.test(s)) {
    return s.replace(/\sE$/, ' Eb/s');
  }
  return `${s} b/s`;
}

// TODO: evaluate if this is required
// updateMaxYPercentage({ chart: this.chart, updateAxis: 1 });

const formatAxis = ({
  xAxis,
  yAxis,
  tooltip,
  data,
  metricInfo,
  moment = defaultMoment,
  logAxis = false,
}) => {
  const unit = ['%', '/1k'].includes(metricInfo.unit) ? metricInfo.unit : '';
  const diff = data.length ? moment(data[data.length - 1][0]).diff(data[0][0]) : 0;
  const xAxisLabelFormat = getAxisLabelFormat(diff);

  const xA = xAxis[0];
  const yA = yAxis[0];

  xA.axisLabel.formatter = (value) => moment(value).format(xAxisLabelFormat);
  yA.axisLabel.formatter = (value) =>
    metricInfo.unit === 'bps' ? formatBpsYAxisLabel(value) : humanize(value) + unit;
  // only one tooltip component
  tooltip[0].formatter = (params) =>
    tooltipFormatter({ params, unit: metricInfo.unit, diff, metricInfo, moment });
  if (logAxis) {
    yA.type = 'log';
    yA.min = 1;
    yA.splitNumber = 0; // default/auto, otherwise scale is off
  } else {
    yA.type = 'value';
    yA.splitNumber = 1;
  }
};

export const buildMetricSeries = ({
  option,
  data,
  metric,
  params,
  allMetrics,
  moment = defaultMoment as any,
  barChart = false,
  logAxis = false,
}) => {
  const metricInfo = allMetrics[metric] || {};
  const { filterColor } = params;
  const { tooltip } = option;
  const timeAxisIndex = findAxisIndex(option.xAxis, TIME_AXIS);
  const valueAxisIndex = findAxisIndex(option.yAxis, VALUE_AXIS);

  const extra: any = {};
  // highlight if there's only one data point
  if (data.length === 1) {
    extra.showSymbol = true;
    extra.symbolSize = 8;
    extra.hoverAnimation = false;
  }

  const metricChartData = {
    id: METRIC_SERIES,
    name: 'Primary',
    type: barChart ? 'bar' : 'line',
    barGap: '-100%', // for overlap/stacking
    data,
    symbol: 'circle',
    showSymbol: false,
    xAxisIndex: timeAxisIndex,
    yAxisIndex: valueAxisIndex,
    showBackground: true,
    itemStyle: barChart
      ? {
          color: transparentize(0.8, filterColor),
          opacity: 1,
          borderColor: filterColor,
          borderWidth: 1,
        }
      : {
          color: filterColor,
        },
    lineStyle: {
      width: 1.5,
    },
    areaStyle: {
      opacity: 0.2,
    },
    markLine: {},
    markArea: {},
    triggerLineEvent: true,
    // connectNulls: logAxis,
    ...extra,
  };

  const xAxis = [option.xAxis[timeAxisIndex]];
  const yAxis = [option.yAxis[valueAxisIndex]];

  formatAxis({
    xAxis,
    yAxis,
    tooltip,
    data,
    metricInfo,
    moment,
    logAxis,
  });

  return { metricChartData, xAxis, yAxis, tooltip };
};
